import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import { useTheme } from "@material-ui/core/styles";
import CardGiftcardIcon from "@material-ui/icons/CardGiftcard";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import ScheduleIcon from "@material-ui/icons/Schedule";
import PaymentIcon from "@material-ui/icons/Payment";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import ShoppingBasketIcon from "@material-ui/icons/ShoppingBasket";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { formatDate, money, moneyCompact, monthLabel, percentLabel } from "./helpers";
import { KpiTile, PageFrame, tNegocio, useMobile } from "./shared";
import { useStyles } from "./styles";
import { useNegocioResource } from "./Lists";
import { businessSetupIntro, localizedBusinessSetup } from "../../content/businessSetup";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useGuide } from "../../context/Guide/GuideContext";
import usePlans from "../../hooks/usePlans";
import { planFlag } from "../../hooks/useGettingStarted/guide";
import CodeLookupLog from "./CodeLookupLog";


const locale = () => (i18n.language || "es").slice(0, 2);

const Delta = ({ value, classes }) => {
  const n = Number(value || 0);
  const up = n >= 0;
  return (
    <div className={`${classes.kpiDelta} ${up ? classes.kpiDeltaUp : classes.kpiDeltaDown}`}>
      {percentLabel(n)} {tNegocio("pages.dashboard.vsPrev")}
    </div>
  );
};

export default function NegocioDashboard() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const [plan, setPlan] = useState(undefined);
  const language = i18n.language || "es";
  const { openGuide } = useGuide();
  const setup = localizedBusinessSetup(language).filter((step) => step.key !== "agent" || planFlag(plan, "useOpenAi"));
  const theme = useTheme();
  const isMobile = useMobile();
  const history = useHistory();
  const canSeeCodeLog = !!user?.super || ["admin", "supervisor"].includes(user?.profile);
  useEffect(() => {
    if (!user?.companyId) return undefined;
    let active = true;
    getPlanCompany(undefined, user.companyId).then((data) => { if (active) setPlan(data?.plan || null); }).catch(() => { if (active) setPlan(null); });
    return () => { active = false; };
  }, [user?.companyId]);
  const { value: summary, loading } = useNegocioResource("summary", async () => {
    const { data } = await api.get("/negocio/summary");
    return data;
  });

  const chartData = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    return (summary?.monthSeries || [])
      .filter((row) => Number(row.year) === year && Number(row.month) <= month)
      .map((row) => ({
        ...row,
        label: monthLabel(row.month, locale()),
      }));
  }, [summary]);

  const cash = (n) => (isMobile ? moneyCompact(n) : money(n));
  const filledMonths = chartData.filter((row) => Number(row.profit));
  const best = filledMonths.reduce((acc, row) => (!acc || row.profit > acc.profit ? row : acc), null);
  const worst = filledMonths.reduce((acc, row) => (!acc || row.profit < acc.profit ? row : acc), null);
  const rangeTotal = cash(chartData.reduce((acc, row) => acc + Number(row.profit || 0), 0));

  return (
    <PageFrame title={tNegocio("pages.dashboard.title")} subtitle={tNegocio("pages.dashboard.subtitle")} guide={{ id: "negocio", to: "/negocio", startId: "order" }}>
      <div className={classes.sectionTitle}>{tNegocio("pages.dashboard.monthResume")}</div>
      <div className={classes.kpiGrid}>
        <KpiTile icon={ArrowUpwardIcon} tone="kpiToneGood" label={tNegocio("kpi.monthIncome")} value={loading ? "…" : cash(summary?.monthIncome)} onClick={() => history.push("/negocio/ingresos")} />
        <KpiTile icon={TrendingDownIcon} tone="kpiToneBad" label={tNegocio("kpi.monthCost")} value={loading ? "…" : cash(summary?.monthCost)} onClick={() => history.push("/negocio/ingresos")} />
        <KpiTile icon={TrendingUpIcon} label={tNegocio("kpi.monthProfit")} value={loading ? "…" : cash(summary?.monthProfit)} hint={<Delta value={summary?.monthProfitChange} classes={classes} />} onClick={() => history.push("/negocio/ingresos")} />
      </div>

      {summary?.raffleWinner ? (
        <>
          <div className={classes.sectionTitle}>{tNegocio("pages.dashboard.raffleTitle")}</div>
          <div
            className={classes.alertBanner}
            style={{
              marginBottom: 16,
              borderColor: summary.raffleWinner.redeemed
                ? "#86efac"
                : summary.raffleWinner.expired
                  ? "#fecaca"
                  : "#fde68a",
              background: summary.raffleWinner.redeemed
                ? "linear-gradient(135deg, #f0fdf4, #fff)"
                : summary.raffleWinner.expired
                  ? "linear-gradient(135deg, #fef2f2, #fff)"
                  : "linear-gradient(135deg, #fffbeb, #fff)",
            }}
            onClick={() =>
              summary.raffleWinner.winnerContactId
                ? history.push(`/negocio/clientes?contactId=${summary.raffleWinner.winnerContactId}`)
                : null
            }
            role="button"
            tabIndex={0}
          >
            <div>
              <div className={classes.alertTitle}>
                <CardGiftcardIcon fontSize="small" style={{ verticalAlign: "middle", marginRight: 6 }} />
                {tNegocio("pages.dashboard.raffleWinner")
                  .replace("{name}", summary.raffleWinner.winnerName || "—")
                  .replace("{month}", summary.raffleWinner.periodMonth || "")}
              </div>
              <div className={classes.alertSub}>
                {[
                  summary.raffleWinner.winnerNumber
                    ? tNegocio("pages.dashboard.rafflePhone").replace("{phone}", summary.raffleWinner.winnerNumber)
                    : null,
                  tNegocio("pages.dashboard.raffleTickets")
                    .replace("{n}", String(summary.raffleWinner.winnerTickets || 0))
                    .replace("{total}", String(summary.raffleWinner.ticketsTotal || 0)),
                  summary.raffleWinner.redeemed
                    ? tNegocio("pages.dashboard.raffleRedeemed").replace(
                        "{date}",
                        formatDate(summary.raffleWinner.redeemedAt) || "—"
                      )
                    : summary.raffleWinner.expired
                      ? tNegocio("pages.dashboard.raffleExpired")
                      : tNegocio("pages.dashboard.rafflePending").replace(
                          "{date}",
                          formatDate(summary.raffleWinner.prizeExpiresAt) || "—"
                        ),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <ChevronRightIcon color="action" />
          </div>
        </>
      ) : null}

      <div className={classes.alertRow}>
        <div
          className={`${classes.alertBanner} ${classes.alertExpired}`}
          onClick={() => history.push("/negocio/cobros")}
          role="button"
          tabIndex={0}
        >
          <div>
            <div className={classes.alertTitle}>
              <EventBusyIcon fontSize="small" style={{ verticalAlign: "middle", marginRight: 6 }} />
              {tNegocio("pages.dashboard.expiredBanner").replace("{n}", String(summary?.expiredAlert?.count || 0))}
            </div>
            <div className={classes.alertSub}>
              {money(summary?.expiredAlert?.amount)} {tNegocio("pages.dashboard.toRenew")}
            </div>
          </div>
          <ChevronRightIcon color="action" />
        </div>
        <div
          className={`${classes.alertBanner} ${classes.alertExpiring}`}
          onClick={() => history.push("/negocio/cobros")}
          role="button"
          tabIndex={0}
        >
          <div>
            <div className={classes.alertTitle}>
              <ScheduleIcon fontSize="small" style={{ verticalAlign: "middle", marginRight: 6 }} />
              {tNegocio("pages.dashboard.expiringBanner").replace("{n}", String(summary?.expiringAlert?.count || 0))}
            </div>
            <div className={classes.alertSub}>
              {money(summary?.expiringAlert?.amount)} {tNegocio("pages.dashboard.toRenew")}
            </div>
          </div>
          <ChevronRightIcon color="action" />
        </div>
        <div
          className={`${classes.alertBanner} ${classes.alertPending}`}
          onClick={() => history.push("/negocio/ingresos?status=pending_payment")}
          role="button"
          tabIndex={0}
        >
          <div>
            <div className={classes.alertTitle}>
              <PaymentIcon fontSize="small" style={{ verticalAlign: "middle", marginRight: 6 }} />
              {tNegocio("pages.dashboard.pendingBanner").replace("{n}", String(summary?.pendingAlert?.count || 0))}
            </div>
            <div className={classes.alertSub}>
              {money(summary?.pendingAlert?.amount)} {tNegocio("pages.dashboard.toCollect")}
            </div>
          </div>
          <ChevronRightIcon color="action" />
        </div>
        <div
          className={`${classes.alertBanner} ${classes.alertPending}`}
          onClick={() => history.push("/negocio/pedidos")}
          role="button"
          tabIndex={0}
        >
          <div>
            <div className={classes.alertTitle}>
              <AssignmentTurnedInIcon fontSize="small" style={{ verticalAlign: "middle", marginRight: 6 }} />
              {tNegocio("pages.dashboard.reviewBanner").replace("{n}", String(summary?.reviewAlert?.count || 0))}
            </div>
            <div className={classes.alertSub}>{tNegocio("pages.dashboard.toReview")}</div>
          </div>
          <ChevronRightIcon color="action" />
        </div>
        <div
          className={`${classes.alertBanner} ${classes.alertExpiring}`}
          onClick={() => history.push("/negocio/pedidos")}
          role="button"
          tabIndex={0}
        >
          <div>
            <div className={classes.alertTitle}>
              <ShoppingBasketIcon fontSize="small" style={{ verticalAlign: "middle", marginRight: 6 }} />
              {tNegocio("pages.dashboard.ordersBanner").replace("{n}", String(summary?.ordersAlert?.count || 0))}
            </div>
            <div className={classes.alertSub}>{tNegocio("pages.dashboard.toOrders")}</div>
          </div>
          <ChevronRightIcon color="action" />
        </div>
        <div
          className={`${classes.alertBanner} ${classes.alertExpired}`}
          onClick={() => history.push("/negocio/configuracion/tienda?seccion=soporte")}
          role="button"
          tabIndex={0}
        >
          <div>
            <div className={classes.alertTitle}>
              <HeadsetMicIcon fontSize="small" style={{ verticalAlign: "middle", marginRight: 6 }} />
              {tNegocio("pages.dashboard.supportBanner").replace("{n}", String(summary?.supportAlert?.count || 0))}
            </div>
            <div className={classes.alertSub}>{tNegocio("pages.dashboard.toSupport")}</div>
          </div>
          <ChevronRightIcon color="action" />
        </div>
      </div>

      <div className={classes.dashPanel} style={{ marginBottom: 16 }}>
        <div>
          <div className={classes.sectionTitle} style={{ marginTop: 0 }}>
            {tNegocio("pages.dashboard.profitByMonth")}
          </div>
          <div className={classes.chartMeta}>
            <div className={classes.chartMetaItem}>
              <div className={classes.chartMetaLabel}>{tNegocio("pages.dashboard.rangeTotal")}</div>
              <div>{rangeTotal}</div>
            </div>
            <div className={classes.chartMetaItem}>
              <div className={classes.chartMetaLabel}>{tNegocio("pages.dashboard.bestMonth")}</div>
              <div>
                {best
                  ? `${monthLabel(best.month, locale())} ${String(best.year).slice(2)} · ${money(best.profit)}`
                  : "—"}
              </div>
            </div>
            <div className={classes.chartMetaItem}>
              <div className={classes.chartMetaLabel}>{tNegocio("pages.dashboard.worstMonth")}</div>
              <div>
                {worst
                  ? `${monthLabel(worst.month, locale())} ${String(worst.year).slice(2)} · ${money(worst.profit)}`
                  : "—"}
              </div>
            </div>
          </div>
          <div style={{ width: "100%", height: isMobile ? 220 : 240 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: isMobile ? 4 : 0, bottom: isMobile ? 12 : 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis
                  dataKey="label"
                  stroke={theme.palette.text.secondary}
                  interval={isMobile ? "preserveStartEnd" : 0}
                  angle={isMobile ? -30 : 0}
                  textAnchor={isMobile ? "end" : "middle"}
                  height={isMobile ? 42 : 28}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis
                  width={isMobile ? 36 : 48}
                  stroke={theme.palette.text.secondary}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  tickFormatter={(v) =>
                    Number(v).toLocaleString("es-CO", { notation: "compact", maximumFractionDigits: 1 })
                  }
                />
                <Tooltip formatter={(value) => money(value)} />
                <Line
                  type="monotone"
                  dataKey="profit"
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  isAnimationActive
                  animationDuration={800}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      {canSeeCodeLog ? <CodeLookupLog /> : null}

      <div className={classes.sectionTitle}>{tNegocio("pages.dashboard.setup")}</div>
      <div className={classes.kpiHint} style={{ marginBottom: 16 }}>{businessSetupIntro[locale()] || businessSetupIntro.es}</div>
      <div className={classes.setupGrid}>
        {setup.map((step, index) => {
          const done = step.metric && Number(summary?.[step.metric] || 0) > 0;
          return (
            <div
              key={step.key}
              className={`${classes.setupCard} ${done ? classes.setupCardDone : ""}`}
              onClick={() => openGuide({ id: step.key, title: step.title, to: step.to })}
              onKeyDown={(event) => { if (event.key === "Enter") openGuide({ id: step.key, title: step.title, to: step.to }); }}
              role="button"
              tabIndex={0}
            >
              <div className={classes.setupStep}>
                {done ? (
                  <CheckCircleIcon style={{ fontSize: 16, verticalAlign: "middle", marginRight: 4 }} />
                ) : !step.metric ? (
                  <InfoOutlinedIcon style={{ fontSize: 16, verticalAlign: "middle", marginRight: 4 }} />
                ) : (
                  <RadioButtonUncheckedIcon style={{ fontSize: 16, verticalAlign: "middle", marginRight: 4 }} />
                )}
                {tNegocio("pages.dashboard.step")} {index + 1}
              </div>
              <div className={classes.setupTitle}>{step.title}</div>
              <div className={classes.setupDesc}>{step.desc}</div>
              <Button
                size="small"
                color="primary"
                variant={done ? "outlined" : "contained"}
                onClick={(event) => { event.stopPropagation(); openGuide({ id: step.key, title: step.title, to: step.to }); }}
              >
                {i18n.t("gettingStarted.guide")}
              </Button>
            </div>
          );
        })}
      </div>
    </PageFrame>
  );
}
